import React, { useContext, useEffect, useRef, useState, memo } from 'react';
import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import dayjs from 'dayjs';
import axios from 'axios';
import { useAppContext } from '../../../providers/AppProvider';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  DataZoomComponent
} from 'echarts/components';
import { LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { Card } from 'react-bootstrap';
import { MachineDataContext } from '../../../providers/MachineDataContext';
import { FallingLines } from 'react-loader-spinner';
import { isLiveTimeRange } from '../utils/chartUtils';
import { INFLUX_API } from '../../../config/api';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LineChart,
  CanvasRenderer,
  LegendComponent,
  DataZoomComponent
]);

const getDefaultOptions = (theme, getThemeColor, data) => {
  const sortedMeterAct = data.Forverder_Act_Pos.sort(
    (a, b) => new Date(a[0]) - new Date(b[0])
  );

  const values = sortedMeterAct.map(item => item[1]); // Extract only the values (ignoring the timestamps)
  const dynamicMin = Math.min(...values).toFixed(2);
  const dynamicMax = (Math.max(...values) * 1.1).toFixed(2);

  return {
    color: [getThemeColor('primary'), getThemeColor('info')],
    title: {
      text: 'Machine Ferveder Chart'
    },
    tooltip: {
      trigger: 'axis',
      padding: 10,
      backgroundColor: getThemeColor('gray-100'),
      borderColor: getThemeColor('gray-300'),
      textStyle: { color: getThemeColor('dark') },
      borderWidth: 1,
      transitionDuration: 0,
      axisPointer: {
        type: 'line',
        axis: 'x'
      },
      formatter: function (params) {
        const value = params[0].value[1];
        const time = params[0].value[0];
        return `Time: ${dayjs(time).format(
          'HH:mm:ss'
        )}<br/>Value: ${value.toFixed(2)}`;
      }
    },
    xAxis: {
      type: 'time',
      boundaryGap: false,
      axisLabel: {
        formatter: value => dayjs(value).format('HH:mm'),
        interval: 'auto',
        margin: 10
      },
      splitLine: {
        show: false
      }
    },
    yAxis: {
      position: 'right',
      axisPointer: { type: 'none' },
      axisTick: { show: false },
      splitLine: {
        show: false
      },
      axisLine: { show: false },
      axisLabel: { show: true },
      min: -260,
      max: 690
    },
    series: [
      {
        type: 'line',
        data: data.Forverder_Act_Pos,
        showSymbol: false,
        symbol: 'circle',
        smooth: true
      },
      {
        type: 'line',
        data: data.Forverder_Left_Limit,
        lineStyle: {
          type: 'dashed',
          width: 1,
          color: getThemeColor('info')
        },
        showSymbol: false,
        symbol: 'circle'
      },
      {
        type: 'line',
        data: data.Forverder_Right_Limit,
        lineStyle: {
          type: 'dashed',
          width: 1,
          color: getThemeColor('info')
        },
        showSymbol: false,
        symbol: 'circle'
      }
    ],
    grid: {
      right: 20,
      left: 20,
      bottom: '20px',
      top: '2%',
      containLabel: true
    },
    animation: true,
    dataZoom: [
      {
        type: 'inside',
        xAxisIndex: 0,
        start: 0,
        end: 100
      }
    ]
  };
};

const MachineCarrierChart = ({
  bucket,
  machineName,
  fields,
  startTime,
  endTime
}) => {
  const chartRef = useRef(null);
  const [loading, setLoading] = useState(true);

  const [data, setData] = useState({
    Forverder_Right_Limit: [],
    Forverder_Left_Limit: [],
    Forverder_Act_Pos: []
  });

  const {
    config: { theme },
    getThemeColor
  } = useAppContext();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${INFLUX_API}/api/influx/${machineName}/fields?fields=${fields}&start=${startTime}&end=${endTime}&bucket=${bucket}`
      );

      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const intervalId = isLiveTimeRange(endTime) ? setInterval(fetchData, 5000) : null;
    return () => clearInterval(intervalId);
  }, [startTime, endTime]);

  if (!data.Forverder_Act_Pos.length) {
    return (
      <Card className="h-100">
        <Card.Body style={{ height: '316px', width: '100%' }}>
          <div style={styles.spinnerContainer}>
            <FallingLines
              color={theme === 'dark' ? 'white' : '#3874ff'}
              width="100"
              visible={true}
              ariaLabel="falling-circles-loading"
              // wrapperStyle ={styles.spinnerContainer}
            />
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="h-100">
      <Card.Body>
        <ReactEChartsCore
          ref={chartRef}
          echarts={echarts}
          option={getDefaultOptions(theme, getThemeColor, data)}
          style={{ height: '316px', width: '100%' }}
        />
      </Card.Body>
    </Card>
  );
};

const styles = {
  spinnerContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%', // Make sure the container takes up the full height of the Card body
    width: '100%' // Ensure the container takes up the full width of the Card body
  }
};

export default memo(MachineCarrierChart);
